import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useShop } from '../context/ShopContext';
import { 
  Calculator, 
  Sparkles, 
  CheckCircle2, 
  TrendingDown, 
  ArrowRight, 
  ShieldCheck, 
  Scale, 
  MessageCircle,
  Award,
  Crown
} from 'lucide-react';
import { formatINR } from '../data/products';
import { JEWELLERY_TIERS } from '../components/WastageCalculatorModal';

export default function WastageCalculatorPage() {
  const { 
    masterMetalRates, 
    liveGoldRates,
    storeSettings,
    addToast
  } = useShop();

  const [purity, setPurity] = useState('22KT');
  const [weight, setWeight] = useState(25);
  const [selectedTierId, setSelectedTierId] = useState('plain');

  // Resolve rates dynamically
  const rate24K = masterMetalRates?.gold?.find((g) => g.label?.includes('24') || g.id?.includes('24'))?.rate || liveGoldRates?.gold24K || 7380;
  const rate22K = masterMetalRates?.gold?.find((g) => g.label?.includes('22') || g.id?.includes('22'))?.rate || liveGoldRates?.gold22K || 6765;
  const rate18K = masterMetalRates?.gold?.find((g) => g.label?.includes('18') || g.id?.includes('18'))?.rate || liveGoldRates?.gold18K || 5535;

  let currentRate = rate22K;
  if (purity === '24KT') currentRate = rate24K;
  else if (purity === '18KT') currentRate = rate18K;

  const currentTier = JEWELLERY_TIERS.find((t) => t.id === selectedTierId) || JEWELLERY_TIERS[0];

  const parsedWeight = Math.max(0.5, parseFloat(weight) || 1);
  const rawMetalValue = Math.round(parsedWeight * currentRate);

  // Traditional Jeweller calculation
  const marketWastageAmt = Math.round(rawMetalValue * (currentTier.marketWastage / 100));
  const marketTaxable = rawMetalValue + marketWastageAmt;
  const marketGst = Math.round(marketTaxable * 0.03);
  const marketTotal = marketTaxable + marketGst;

  // ML Jewellers 24KT Club calculation
  const ourWastageAmt = Math.round(rawMetalValue * (currentTier.ourWastage / 100));
  const ourTaxable = rawMetalValue + ourWastageAmt;
  const ourGst = Math.round(ourTaxable * 0.03);
  const ourTotal = ourTaxable + ourGst;

  const totalSavings = marketTotal - ourTotal;
  const wastageSavings = marketWastageAmt - ourWastageAmt;
  const savingsPercent = Math.round((totalSavings / marketTotal) * 100);

  const handleShareQuote = () => {
    const text = `💎 *ML JEWELLERS - 24KT CLUB WASTAGE CALCULATION* 💎%0A` +
      `Category: ${currentTier.name}%0A` +
      `Purity: ${purity} Gold @ ₹${currentRate.toLocaleString('en-IN')}/gm%0A` +
      `Gross Weight: ${parsedWeight} grams%0A` +
      `Raw Gold Value: ₹${rawMetalValue.toLocaleString('en-IN')}%0A%0A` +
      `🔴 Traditional Showroom Total: ₹${marketTotal.toLocaleString('en-IN')} (${currentTier.marketWastage}% Wastage)%0A` +
      `🟢 ML Jewellers 24KT Club: ₹${ourTotal.toLocaleString('en-IN')} (${currentTier.ourWastage}% Wastage)%0A` +
      `🎉 *TOTAL NET SAVINGS: ₹${totalSavings.toLocaleString('en-IN')} (${savingsPercent}%)*%0A%0A` +
      `Please confirm stock availability for this specification.`;

    const whatsappNum = storeSettings?.supportWhatsapp || '919876543210';
    window.open(`https://wa.me/${whatsappNum}?text=${text}`, '_blank');
  };

  return (
    <div style={{ minHeight: '80vh', backgroundColor: '#07070b', color: '#fff', padding: '2rem 1rem 5rem' }}>
      <div style={{ maxWidth: '1240px', margin: '0 auto' }}>
        {/* Breadcrumbs */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '1.75rem' }}>
          <Link to="/" style={{ color: 'var(--gold-400)', textDecoration: 'none' }}>Home</Link>
          <span>/</span>
          <span style={{ color: '#fff' }}>Live Gold Wastage & Making Charges Calculator</span>
        </div>

        {/* Hero Banner */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.15) 0%, rgba(12, 12, 20, 0.95) 100%)',
          borderRadius: '20px',
          border: '1px solid var(--border-gold)',
          padding: '2.5rem 2rem',
          marginBottom: '2.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1.5rem'
        }}>
          <div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '4px 12px', borderRadius: '999px', backgroundColor: 'rgba(212, 175, 55, 0.15)', border: '1px solid var(--gold-400)', color: 'var(--gold-400)', fontSize: '0.78rem', fontWeight: 700, marginBottom: '0.75rem' }}>
              <Calculator size={14} /> JewelCasa Live Price Engine
            </div>
            <h1 style={{ fontSize: '2.2rem', fontWeight: 800, color: '#fff', marginBottom: '0.75rem' }}>
              Gold Wastage & Value Comparison Calculator
            </h1>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem', maxWidth: '750px', lineHeight: 1.6 }}>
              Compare making charges and wastage margins in real-time. Calculate exact savings on bridal chokers, bangles, solitaires, and chains before you buy.
            </p>
          </div>

          <Link 
            to="/wastage-list" 
            className="btn btn-secondary"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', textDecoration: 'none' }}
          >
            <Crown size={16} /> View 24KT Rate Card
          </Link>
        </div>

        {/* Calculator Main Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '2rem', alignItems: 'start' }}>
          {/* Controls Card */}
          <div style={{
            backgroundColor: '#0c0c14',
            borderRadius: '20px',
            border: '1px solid var(--border-gold)',
            padding: '2rem',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.5)'
          }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#fff', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <Scale size={20} color="var(--gold-400)" /> 1. Select Jewellery Parameters
            </h2>

            {/* Purity Tabs */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '0.6rem' }}>
                Select Gold Purity (Hallmark Standard):
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.6rem' }}>
                {[
                  { id: '24KT', label: '24KT Pure (999)', rate: rate24K },
                  { id: '22KT', label: '22KT Royal (916)', rate: rate22K },
                  { id: '18KT', label: '18KT Fine (750)', rate: rate18K }
                ].map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setPurity(item.id)}
                    style={{
                      padding: '0.75rem 0.5rem',
                      borderRadius: '10px',
                      backgroundColor: purity === item.id ? 'rgba(212, 175, 55, 0.15)' : '#12121e',
                      border: purity === item.id ? '1px solid var(--gold-400)' : '1px solid rgba(255, 255, 255, 0.08)',
                      color: purity === item.id ? 'var(--gold-400)' : '#fff',
                      cursor: 'pointer',
                      textAlign: 'center',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{item.id}</div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '2px' }}>₹{item.rate.toLocaleString('en-IN')}/g</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Category Tier Selector */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '0.6rem' }}>
                Jewellery Craft Category:
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                {JEWELLERY_TIERS.map((tier) => (
                  <label
                    key={tier.id}
                    onClick={() => setSelectedTierId(tier.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '0.85rem 1rem',
                      borderRadius: '10px',
                      backgroundColor: selectedTierId === tier.id ? 'rgba(212, 175, 55, 0.12)' : '#12121e',
                      border: selectedTierId === tier.id ? '1px solid var(--gold-400)' : '1px solid rgba(255, 255, 255, 0.06)',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                      <input 
                        type="radio" 
                        name="jewelTier" 
                        checked={selectedTierId === tier.id} 
                        onChange={() => setSelectedTierId(tier.id)}
                      />
                      <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#fff' }}>{tier.name}</span>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{ fontSize: '0.8rem', color: 'var(--gold-400)', fontWeight: 700 }}>
                        {tier.ourWastage}% Wastage
                      </span>
                      <span style={{ fontSize: '0.72rem', color: '#f87171', display: 'block', textDecoration: 'line-through' }}>
                        vs {tier.marketWastage}%
                      </span>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Weight Input & Slider */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem' }}>
                <label style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                  Net Gold Weight:
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <input
                    type="number"
                    min="1"
                    max="1000"
                    step="0.5"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    style={{
                      width: '80px',
                      padding: '0.4rem 0.6rem',
                      backgroundColor: '#12121e',
                      border: '1px solid var(--border-gold)',
                      borderRadius: '6px',
                      color: 'var(--gold-400)',
                      fontSize: '1rem',
                      fontWeight: 700,
                      textAlign: 'center'
                    }}
                  />
                  <span style={{ fontSize: '0.9rem', color: '#fff', fontWeight: 600 }}>Grams</span>
                </div>
              </div>

              <input 
                type="range"
                min="2"
                max="150"
                step="0.5"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                style={{
                  width: '100%',
                  accentColor: 'var(--gold-400)',
                  cursor: 'pointer'
                }}
              />

              {/* Quick weight chips */}
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
                {[8, 15, 25, 50, 100].map((w) => (
                  <button
                    key={w}
                    type="button"
                    onClick={() => setWeight(w)}
                    style={{
                      padding: '4px 10px',
                      borderRadius: '999px',
                      backgroundColor: weight == w ? 'var(--gold-400)' : 'rgba(255, 255, 255, 0.06)',
                      color: weight == w ? '#000' : 'var(--text-muted)',
                      border: 'none',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    {w}g
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Real-Time Comparison Card */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {/* Savings Highlight Box */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(12, 12, 20, 0.95) 100%)',
              border: '1px solid #10b981',
              borderRadius: '20px',
              padding: '1.75rem',
              textAlign: 'center',
              boxShadow: '0 10px 30px rgba(16, 185, 129, 0.15)'
            }}>
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.4rem',
                backgroundColor: 'rgba(16, 185, 129, 0.2)',
                color: '#34d399',
                padding: '4px 12px',
                borderRadius: '999px',
                fontSize: '0.8rem',
                fontWeight: 700,
                marginBottom: '0.75rem'
              }}>
                <TrendingDown size={16} /> YOU SAVE INSTANTLY
              </span>
              <div style={{ fontSize: '2.5rem', fontWeight: 900, color: '#34d399', marginBottom: '0.3rem' }}>
                {formatINR(totalSavings)}
              </div>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                Directly saved on making charges & bullion wastage markup ({savingsPercent}% reduction)
              </p>
            </div>

            {/* Side-by-Side Comparison Table */}
            <div style={{
              backgroundColor: '#0c0c14',
              borderRadius: '20px',
              border: '1px solid var(--border-gold)',
              padding: '1.75rem'
            }}>
              <h3 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#fff', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Sparkles size={18} color="var(--gold-400)" /> Side-by-Side Cost Audit
              </h3>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                {/* Traditional Retail Jeweller */}
                <div style={{
                  backgroundColor: '#12121e',
                  borderRadius: '12px',
                  padding: '1.25rem',
                  border: '1px solid rgba(248, 113, 113, 0.3)'
                }}>
                  <div style={{ fontSize: '0.75rem', color: '#f87171', fontWeight: 700, textTransform: 'uppercase', marginBottom: '0.5rem' }}>
                    Traditional Showroom
                  </div>
                  <div style={{ fontSize: '1.3rem', fontWeight: 800, color: '#fff', marginBottom: '0.75rem' }}>
                    {formatINR(marketTotal)}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                    <div>Pure Gold: {formatINR(rawMetalValue)}</div>
                    <div>Wastage ({currentTier.marketWastage}%): <span style={{ color: '#f87171' }}>{formatINR(marketWastageAmt)}</span></div>
                    <div>GST (3%): {formatINR(marketGst)}</div>
                  </div>
                </div>

                {/* ML Jewellers 24KT Club */}
                <div style={{
                  backgroundColor: 'rgba(212, 175, 55, 0.08)',
                  borderRadius: '12px',
                  padding: '1.25rem',
                  border: '1px solid var(--gold-400)'
                }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--gold-400)', fontWeight: 700, textTransform: 'uppercase', marginBottom: '0.5rem' }}>
                    ML Jewellers 24KT Club
                  </div>
                  <div style={{ fontSize: '1.3rem', fontWeight: 800, color: 'var(--gold-400)', marginBottom: '0.75rem' }}>
                    {formatINR(ourTotal)}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                    <div>Pure Gold: {formatINR(rawMetalValue)}</div>
                    <div>Wastage ({currentTier.ourWastage}%): <span style={{ color: '#34d399' }}>{formatINR(ourWastageAmt)}</span></div>
                    <div>GST (3%): {formatINR(ourGst)}</div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <button
                  type="button"
                  onClick={handleShareQuote}
                  className="btn"
                  style={{
                    backgroundColor: '#25D366',
                    color: '#fff',
                    padding: '0.85rem',
                    borderRadius: '10px',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                >
                  <MessageCircle size={18} /> Send Breakdown to WhatsApp
                </button>

                <Link
                  to="/category/all"
                  className="btn btn-gold"
                  style={{
                    padding: '0.85rem',
                    borderRadius: '10px',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    textDecoration: 'none'
                  }}
                >
                  Shop Catalogue at These Rates <ArrowRight size={18} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
